// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook for fetching and managing event groups associated with an asset.
 * This module provides a convenient way to request and cache event group data
 * from the remote API using React Query, with support for filtering and
 * conditional execution.
 */
import {
  EventGroupsParameters,
  getEventGroups,
} from '@/asset/api/get-event-groups';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

/**
 * A React hook that fetches and caches event groups for a specific asset.
 * Uses React Query for automatic caching, background updates, and stale data
 * handling. The hook maintains previous data while fetching updates to prevent
 * UI flicker.
 *
 * @param assetKey - Unique identifier of the asset to fetch event groups for
 * @param parameters - Filter parameters for the event groups query
 * @param parameters.start - Optional start date for filtering groups
 * @param parameters.end - Optional end date for filtering groups
 * @param isEnabled - Whether the query should execute (defaults to true)
 * @returns React Query result object containing:
 * @property {EventGroup[]} data.eventGroups - The fetched event groups
 * @property {boolean} isLoading - Whether the initial fetch is in progress
 * @property {boolean} isFetching - Whether any fetch is in progress
 * @property {boolean} isError - Whether the last fetch attempt resulted in an error
 * @property {function} refetch - Function to manually trigger a refresh
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export function useEventGroups(
  assetKey: string,
  parameters: EventGroupsParameters,
  isEnabled = true
) {
  return useQuery({
    queryKey: ['asset', assetKey, 'event-group', parameters],
    queryFn: () => getEventGroups(assetKey, parameters),
    enabled: isEnabled,
    placeholderData: keepPreviousData,
  });
}
